import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bygningsdelAPI, bildeAPI } from '../services/api';
import BildeOpplaster from '../components/BildeOpplaster';
import BildeGalleri from '../components/BildeGalleri';
import { lagTekVeiledning } from '../utils/tek-krav';
import NS3600Tekst from '../components/NS3600Tekst';
import HelpButton from '../components/HelpButton';
import './Befaring.css'; // Gjenbruker Befaring.css for styling

function BygningsdelBefaring() {
  const { id } = useParams(); // bygningsdel ID
  const navigate = useNavigate();
  const [bygningsdel, setBygningsdel] = useState(null);
  const [sporsmal, setSporsmal] = useState([]);
  const [svar, setSvar] = useState({});
  const [ikkeBesvartBegrunnelse, setIkkeBesvartBegrunnelse] = useState({});
  const [tgiuBegrunnelse, setTgiuBegrunnelse] = useState({}); // Årsak for TGIU-valg
  const [kommentarer, setKommentarer] = useState({}); // Kommentarer for alle TG-spørsmål
  const [aarsakKonsekvens, setAarsakKonsekvens] = useState({}); // §2-22: Årsak og konsekvens for TG2/TG3
  const [kostnadsanslag, setKostnadsanslag] = useState({}); // §2-22: Kostnadsanslag for TG3
  const [kostnadsestimat, setKostnadsestimat] = useState({
    kategori: '',
    eksakt: '',
    beskrivelse: '',
    usikkerhet: 'middels',
    anbefaling: ''
  }); // Detaljert kostnadsestimat for bygningsdel
  const [typeKommentar, setTypeKommentar] = useState(''); // Type-spesifikasjon (f.eks. "Taktekking: Teglstein")
  const [renoveringsAar, setRenoveringsAar] = useState(''); // Renoveringsår for TEK-vurdering
  const [aiValidering, setAiValidering] = useState({}); // AI-validering av årsak/konsekvens
  const [aiValiderer, setAiValiderer] = useState({}); // Loading state for AI-validering
  const [bilder, setBilder] = useState([]);
  const [sporsmalBilder, setSporsmalBilder] = useState({}); // Spørsmål-spesifikke bilder
  const [tgiuAiForslag, setTgiuAiForslag] = useState({}); // AI-forslag for TGIU-begrunnelser per spørsmål
  const [tgiuAiLoading, setTgiuAiLoading] = useState({}); // Loading state for AI-forslag
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingBilde, setUploadingBilde] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [visParagraf218Info, setVisParagraf218Info] = useState(false); // Vis § 2-18 info-dialog

  // Beregn TEK-veiledning basert på byggeår og renoveringsår
  const tekVeiledning = useMemo(() => {
    if (!bygningsdel) return null;
    const byggeaar = bygningsdel.byggeaar; // Antar byggeår kommer fra bygningsdel-objektet
    const renoveringsaar = renoveringsAar ? parseInt(renoveringsAar) : null;
    return lagTekVeiledning(bygningsdel.bygningsdel_type, byggeaar, renoveringsaar);
  }, [bygningsdel, renoveringsAar]);

  // Helper: Identifiser oppfølgingsspørsmål (nested questions)
  const getOppfolgingMapping = useMemo(() => {
    const mapping = {};

    sporsmal.forEach((s) => {
      const tekst = s.sporsmal_tekst.toLowerCase();

      // "Resultat av hulltaking" følger "Er hulltaking utført"
      if (tekst.includes('resultat') && tekst.includes('hulltaking')) {
        const parentQ = sporsmal.find(q =>
          q.sporsmal_tekst.toLowerCase().includes('hulltaking') &&
          q.sporsmal_tekst.toLowerCase().includes('utført')
        );
        if (parentQ) {
          mapping[s.id] = { parentId: parentQ.id, showWhen: 'Ja' };
        }
      }

      // "Kontroll av tetningsmembran" følger "Type tetningsmembran"
      if (tekst.includes('kontroll') && tekst.includes('tetningsmembran')) {
        const parentQ = sporsmal.find(q =>
          q.sporsmal_tekst.toLowerCase().includes('type') &&
          q.sporsmal_tekst.toLowerCase().includes('tetningsmembran')
        );
        if (parentQ) {
          mapping[s.id] = { parentId: parentQ.id, showWhen: 'any' }; // Vis alltid når type er valgt
        }
      }

      // Generisk: "Beskrivelse av eventuelle skader/feil" følger ja/nei-spørsmål om skader
      if (tekst.includes('beskrivelse av eventuelle')) {
        // Finn ALLE ja/nei-spørsmål om skader/feil som kommer før dette feltet
        const candidateParents = sporsmal.filter(q => {
          const parentTekst = q.sporsmal_tekst.toLowerCase();
          return q.svar_type === 'ja_nei' &&
                 (parentTekst.includes('skade') ||
                  parentTekst.includes('feil') ||
                  parentTekst.includes('mangler') ||
                  parentTekst.includes('lekkasje') ||
                  parentTekst.includes('kondens') ||
                  parentTekst.includes('råte')) &&
                 q.sortering < s.sortering; // Må komme før beskrivelsesfeltet
        });

        // Bruk det nærmeste (høyeste sortering)
        if (candidateParents.length > 0) {
          const parentQ = candidateParents.sort((a, b) => b.sortering - a.sortering)[0];
          mapping[s.id] = {
            parentId: parentQ.id,
            showWhen: 'Ja',
            multipleParents: candidateParents.map(p => p.id) // Lagre alle for å vise hvis noen = Ja
          };
        }
      }

      // "Dokumentasjon for..." følger "Alder på..." og vises kun hvis alder < 5 år
      if (tekst.includes('dokumentasjon for')) {
        const alderQ = sporsmal.find(q =>
          q.sporsmal_tekst.toLowerCase().includes('alder på') &&
          q.sortering < s.sortering
        );
        if (alderQ) {
          mapping[s.id] = {
            parentId: alderQ.id,
            showWhen: 'lessThan5' // Custom condition for age < 5
          };
        }
      }
    });

    return mapping;
  }, [sporsmal]);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const response = await bygningsdelAPI.getSporsmal(id);
      setBygningsdel(response.data.bygningsdel);
      setSporsmal(response.data.sporsmal);

      // Last type_kommentar hvis den finnes
      if (response.data.bygningsdel.type_kommentar) {
        setTypeKommentar(response.data.bygningsdel.type_kommentar);
      }

      // Last renoverings_aar hvis den finnes
      if (response.data.bygningsdel.renoverings_aar) {
        setRenoveringsAar(response.data.bygningsdel.renoverings_aar);
      }

      // Last bilder
      const bilderResponse = await bygningsdelAPI.getBilder(id);
      setBilder(bilderResponse.data);

      // Last eksisterende svar hvis de finnes
      // TODO: implementer lasting av eksisterende svar
    } catch (err) {
      setError('Kunne ikke hente befaring data: ' + (err.response?.data?.error || err.message));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const evaluerRegelForSvar = async (sporsmalId, sporsmalTekst, svarVerdi) => {
    // Sjekk om svar inneholder TGIU eller andre trigger-verdier
    if (!svarVerdi || !bygningsdel) return;

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://takstmann-api.swagner.workers.dev';
      const response = await fetch(`${API_URL}/api/evaluer-regler`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          bygningsdel_type: bygningsdel.bygningsdel_type,
          sporsmal_tekst: sporsmalTekst,
          svar: svarVerdi,
          svar_verdi: null,
          eiendom_id: bygningsdel.eiendom_id,
          bygningsdel_id: parseInt(id),
          sporsmal_id: sporsmalId
        })
      });

      if (response.ok) {
        const data = await response.json();

        // Vis melding hvis undersøkelser ble auto-opprettet
        if (data.antall_auto_opprettet > 0) {
          setSuccess(`${data.antall_auto_opprettet} videre undersøkelse(r) opprettet automatisk basert på NS3600-krav`);
          setTimeout(() => setSuccess(''), 5000);
        }
      }
    } catch (error) {
      console.error('Feil ved evaluering av regler:', error);
    }
  };

  const handleSvarChange = (sporsmalId, sporsmalTekst, value) => {
    setSvar({ ...svar, [sporsmalId]: value });
    // Evaluer regler når svar endres
    evaluerRegelForSvar(sporsmalId, sporsmalTekst, value);
    // Hent AI-forslag hvis TGIU er valgt
    if (value && value.includes('TGIU')) {
      fetchTgiuAiForslag(sporsmalId, sporsmalTekst);
    }
  };

  // Hent AI-forslag for TGIU-begrunnelser
  const fetchTgiuAiForslag = async (sporsmalId, sporsmalTekst) => {
    if (!bygningsdel) return;

    setTgiuAiLoading({ ...tgiuAiLoading, [sporsmalId]: true });

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://takstmann-api.swagner.workers.dev';
      const response = await fetch(`${API_URL}/api/tgiu-begrunnelser/forslag`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bygningsdel_type: bygningsdel.bygningsdel_type,
          sporsmal_tekst: sporsmalTekst
        })
      });

      if (response.ok) {
        const data = await response.json();
        setTgiuAiForslag({ ...tgiuAiForslag, [sporsmalId]: data.forslag || [] });
      }
    } catch (error) {
      console.error('Feil ved henting av TGIU AI-forslag:', error);
    } finally {
      setTgiuAiLoading({ ...tgiuAiLoading, [sporsmalId]: false });
    }
  };

  // Registrer bruk av TGIU-begrunnelse (for ML-ranking)
  const registrerTgiuBruk = async (begrunnelseId) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://takstmann-api.swagner.workers.dev';
      await fetch(`${API_URL}/api/tgiu-begrunnelser/${begrunnelseId}/bruk`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Feil ved registrering av TGIU-bruk:', error);
    }
  };

  const validateAarsakKonsekvens = async (sporsmalId, beskrivelse, sporsmal) => {
    if (!beskrivelse || beskrivelse.trim().length < 50) {
      return; // Ikke valider hvis for kort
    }

    setAiValiderer({ ...aiValiderer, [sporsmalId]: true });

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://takstmann-api.swagnesen.workers.dev';
      const response = await fetch(`${API_URL}/api/ai/validate-aarsak-konsekvens`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          beskrivelse: beskrivelse,
          bygningsdelType: bygningsdel?.bygningsdel_type,
          tilstandsgrad: svar[sporsmalId]
        })
      });

      const result = await response.json();
      setAiValidering({ ...aiValidering, [sporsmalId]: result });
    } catch (error) {
      console.error('AI-valideringsfeil:', error);
      // Stille feil - ikke vis feilmelding til bruker
    } finally {
      setAiValiderer({ ...aiValiderer, [sporsmalId]: false });
    }
  };

  const fetchSporsmalBilder = async (sporsmalId) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://takstmann-api.swagner.workers.dev';
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(`${API_URL}/api/bygningsdel/id/${id}/bilder`, { headers });
      if (response.ok) {
        const alleBilder = await response.json();
        const filtrerte = alleBilder.filter(b => b.sporsmal_id === sporsmalId);
        setSporsmalBilder(prev => ({ ...prev, [sporsmalId]: filtrerte }));
      }
    } catch (error) {
      console.error('Feil ved henting av spørsmålsbilder:', error);
    }
  };

  const handleBildeUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validering
    if (!file.type.startsWith('image/')) {
      alert('Kun bildefiler er tillatt');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      alert('Bildet er for stort. Maksimal størrelse er 10MB');
      return;
    }

    try {
      setUploadingBilde(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('beskrivelse', '');

      await bygningsdelAPI.uploadBilde(id, formData);

      // Last bilder på nytt
      const bilderResponse = await bygningsdelAPI.getBilder(id);
      setBilder(bilderResponse.data);

      setSuccess('✓ Bilde lastet opp!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      alert('Kunne ikke laste opp bilde: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploadingBilde(false);
    }
  };

  const handleSlettBilde = async (bildeId) => {
    if (!window.confirm('Er du sikker på at du vil slette dette bildet?')) {
      return;
    }

    try {
      await bildeAPI.delete(bildeId);
      setBilder(bilder.filter(b => b.id !== bildeId));
      setSuccess('✓ Bilde slettet');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      alert('Kunne ikke slette bilde: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Valider obligatoriske spørsmål
      const valideringsFeil = [];
      const obligatoriskeSporsmal = sporsmal.filter(s => s.obligatorisk);
      const manglerSvar = [];
      const manglerTgiuBegrunnelse = [];

      for (const s of obligatoriskeSporsmal) {
        const harSvar = svar[s.id] && svar[s.id].trim() !== '';
        const harBegrunnelse = ikkeBesvartBegrunnelse[s.id] &&
                              ikkeBesvartBegrunnelse[s.id].trim().length >= 10;

        if (!harSvar && !harBegrunnelse) {
          manglerSvar.push(`• ${s.sporsmal_tekst} - Må enten besvares eller ha begrunnelse`);
        }
      }

      // Valider TGIU-begrunnelser (for alle spørsmål, ikke bare obligatoriske)
      const manglerAarsakKonsekvens = [];
      const manglerKostnadsanslag = [];

      for (const s of sporsmal) {
        if (svar[s.id] && svar[s.id].includes('TGIU')) {
          const harTgiuBegrunnelse = tgiuBegrunnelse[s.id] &&
                                    tgiuBegrunnelse[s.id].trim().length >= 10;
          if (!harTgiuBegrunnelse) {
            manglerTgiuBegrunnelse.push(`• ${s.sporsmal_tekst} - TGIU krever begrunnelse (minimum 10 tegn)`);
          }
        }

        // §2-22: Valider årsak og konsekvens for TG2/TG3
        if (svar[s.id] && (svar[s.id].includes('TG2') || svar[s.id].includes('TG3'))) {
          const harAarsakKonsekvens = aarsakKonsekvens[s.id] &&
                                      aarsakKonsekvens[s.id].trim().length >= 50;
          if (!harAarsakKonsekvens) {
            manglerAarsakKonsekvens.push(`• ${s.sporsmal_tekst} - §2-22 krever beskrivelse av årsak og konsekvens (minimum 50 tegn)`);
          }
        }

        // §2-22: Valider kostnadsanslag for TG3
        if (svar[s.id] && svar[s.id].includes('TG3')) {
          const harKostnadsanslag = kostnadsanslag[s.id] && kostnadsanslag[s.id].kategori;
          if (!harKostnadsanslag) {
            manglerKostnadsanslag.push(`• ${s.sporsmal_tekst} - §2-22 krever kostnadsanslag for TG3`);
          }
        }
      }

      if (manglerSvar.length > 0) {
        valideringsFeil.push({
          kategori: 'Obligatoriske spørsmål (merket med *)',
          feil: manglerSvar,
        });
      }

      if (manglerTgiuBegrunnelse.length > 0) {
        valideringsFeil.push({
          kategori: 'TGIU-begrunnelser (påkrevd etter NS3600:2018)',
          feil: manglerTgiuBegrunnelse,
        });
      }

      if (manglerAarsakKonsekvens.length > 0) {
        valideringsFeil.push({
          kategori: '§2-22: Årsak og konsekvens (påkrevd for TG2/TG3)',
          feil: manglerAarsakKonsekvens,
        });
      }

      if (manglerKostnadsanslag.length > 0) {
        valideringsFeil.push({
          kategori: '§2-22: Kostnadsanslag (påkrevd for TG3)',
          feil: manglerKostnadsanslag,
        });
      }

      // Lagre valideringsfeil for senere visning, men tillat alltid lagring

      // Bygg svar-array med TGIU-begrunnelser, kommentarer, årsak/konsekvens og kostnadsanslag
      const svarArray = Object.entries(svar).map(([sporsmalId, verdi]) => {
        let fullSvar = verdi;

        // Hvis TGIU er valgt, legg til begrunnelsen
        if (verdi.includes('TGIU') && tgiuBegrunnelse[sporsmalId]) {
          fullSvar += ` - Årsak: ${tgiuBegrunnelse[sporsmalId]}`;
        }

        // Hvis TG-valg har kommentar, legg til
        if (verdi.startsWith('TG') && kommentarer[sporsmalId]) {
          fullSvar += ` | Kommentar: ${kommentarer[sporsmalId]}`;
        }

        // Hvis TG2/TG3, legg til årsak og konsekvens (§2-22)
        if ((verdi.includes('TG2') || verdi.includes('TG3')) && aarsakKonsekvens[sporsmalId]) {
          fullSvar += ` | §2-22 Årsak og konsekvens: ${aarsakKonsekvens[sporsmalId]}`;
        }

        // Hvis TG3, legg til kostnadsanslag (§2-22)
        if (verdi.includes('TG3') && kostnadsanslag[sporsmalId]) {
          const ka = kostnadsanslag[sporsmalId];
          let kostnadsText = '';

          if (ka.kategori === 'eksakt' && ka.eksakt) {
            kostnadsText = `${parseInt(ka.eksakt).toLocaleString('nb-NO')} NOK (eksakt)`;
          } else if (ka.kategori === 'under_10k') {
            kostnadsText = 'Under 10 000 kr';
          } else if (ka.kategori === '10k_50k') {
            kostnadsText = '10 000 – 50 000 kr';
          } else if (ka.kategori === '50k_100k') {
            kostnadsText = '50 000 – 100 000 kr';
          } else if (ka.kategori === '100k_300k') {
            kostnadsText = '100 000 – 300 000 kr';
          } else if (ka.kategori === 'over_300k') {
            kostnadsText = 'Over 300 000 kr';
          }

          fullSvar += ` | §2-22 Kostnadsanslag: ${kostnadsText}`;

          if (ka.beskrivelse) {
            fullSvar += ` - Omfatter: ${ka.beskrivelse}`;
          }
          if (ka.usikkerhet) {
            const usikkerhetText = ka.usikkerhet === 'lav' ? 'Lav usikkerhet' :
                                  ka.usikkerhet === 'middels' ? 'Middels usikkerhet' :
                                  'Høy usikkerhet';
            fullSvar += ` (${usikkerhetText})`;
          }
          if (ka.anbefaling) {
            fullSvar += ` - Anbefaling: ${ka.anbefaling}`;
          }
        }

        return {
          sporsmal_id: parseInt(sporsmalId),
          svar_verdi: fullSvar,
        };
      });

      const begrunnelserArray = Object.entries(ikkeBesvartBegrunnelse)
        .filter(([_, begrunnelse]) => begrunnelse && begrunnelse.trim() !== '')
        .map(([sporsmalId, begrunnelse]) => ({
          sporsmal_id: parseInt(sporsmalId),
          svar_verdi: `[IKKE BESVART] Begrunnelse: ${begrunnelse}`,
        }));

      await bygningsdelAPI.saveSporsmalSvar(id, {
        svar: [...svarArray, ...begrunnelserArray]
      });

      // Samle all årsak/konsekvens-data fra alle spørsmål (for TG2/TG3)
      const alleAarsaker = Object.entries(aarsakKonsekvens)
        .filter(([sporsmalId, tekst]) => {
          const svarVerdi = svar[sporsmalId];
          return svarVerdi && (svarVerdi.includes('TG2') || svarVerdi.includes('TG3')) && tekst && tekst.trim();
        })
        .map(([_, tekst]) => tekst.trim())
        .join('\n\n');

      // Samle kostnadsanslag fra alle spørsmål (for TG3)
      const alleKostnadsanslag = Object.entries(kostnadsanslag)
        .filter(([sporsmalId, ka]) => {
          const svarVerdi = svar[sporsmalId];
          return svarVerdi && svarVerdi.includes('TG3') && ka && ka.kategori;
        })
        .map(([_, ka]) => {
          let kostnadsText = '';
          if (ka.kategori === 'eksakt' && ka.eksakt) {
            kostnadsText = `${parseInt(ka.eksakt).toLocaleString('nb-NO')} NOK (eksakt)`;
          } else if (ka.kategori === 'under_10k') {
            kostnadsText = 'Under 10 000 kr';
          } else if (ka.kategori === '10k_50k') {
            kostnadsText = '10 000 – 50 000 kr';
          } else if (ka.kategori === '50k_100k') {
            kostnadsText = '50 000 – 100 000 kr';
          } else if (ka.kategori === '100k_300k') {
            kostnadsText = '100 000 – 300 000 kr';
          } else if (ka.kategori === 'over_300k') {
            kostnadsText = 'Over 300 000 kr';
          }

          let detaljer = kostnadsText;
          if (ka.beskrivelse) detaljer += ` - Omfatter: ${ka.beskrivelse}`;
          if (ka.usikkerhet) {
            const usikkerhetText = ka.usikkerhet === 'lav' ? 'Lav usikkerhet' :
                                  ka.usikkerhet === 'middels' ? 'Middels usikkerhet' :
                                  'Høy usikkerhet';
            detaljer += ` (${usikkerhetText})`;
          }
          if (ka.anbefaling) detaljer += ` - Anbefaling: ${ka.anbefaling}`;
          return detaljer;
        })
        .join('\n\n');

      // Finn høyeste kostnadsanslag som numerisk verdi (for sortering/rapportering)
      let kostnadsestimat = null;
      Object.values(kostnadsanslag).forEach(ka => {
        if (ka && ka.kategori === 'eksakt' && ka.eksakt) {
          const eksaktVerdi = parseFloat(ka.eksakt);
          if (!kostnadsestimat || eksaktVerdi > kostnadsestimat) {
            kostnadsestimat = eksaktVerdi;
          }
        }
      });

      // Oppdater status basert på om det er mangler eller ikke
      const nyStatus = valideringsFeil.length === 0 ? 'fullfort' : 'pabegynt';
      await bygningsdelAPI.updateStatus(id, {
        status: nyStatus,
        type_kommentar: typeKommentar,
        renoverings_aar: renoveringsAar ? parseInt(renoveringsAar) : null,
        aarsak: alleAarsaker || null,
        konsekvens: alleAarsaker || null,  // §2-22 krever både årsak OG konsekvens i samme felt
        kostnadsestimat: kostnadsestimat || null
      });

      if (valideringsFeil.length === 0) {
        setSuccess('✅ Svar lagret! Bygningsdelen er fullført og klar for rapportgenerering.');
        setError('');
      } else {
        setSuccess('✓ Svar lagret!');

        // Vis tydelig advarsel om mangler
        let mangelmelding = '⚠️ ADVARSEL: Bygningsdelen har mangler som må utbedres før rapport kan genereres.\n\n';
        valideringsFeil.forEach(v => {
          mangelmelding += `${v.kategori}:\n`;
          v.feil.forEach(f => mangelmelding += `  ${f}\n`);
          mangelmelding += '\n';
        });
        setError(mangelmelding);

        // Spør brukeren om de vil gå til kontrollsenteret
        setTimeout(() => {
          const goToKontroll = window.confirm(
            'Vil du gå til kontrollsenteret for å se alle mangler i rapporten?\n\n' +
            'I kontrollsenteret kan du se nøyaktig hva som mangler og klikke deg direkte til stedene som må utbedres.'
          );
          if (goToKontroll) {
            navigate(`/kontrollsenter/${bygningsdel.eiendom_id}`);
          }
        }, 500);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError('Kunne ikke lagre svar: ' + (err.response?.data?.error || err.message));
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const bygningsdelTypeNavn = (type) => {
    const typer = {
      yttertak: 'Yttertak',
      yttervegger: 'Yttervegger',
      vinduer_dorer: 'Vinduer og dører',
      fundament_sokkel: 'Fundament og sokkel',
      drenering: 'Drenering',
      elektrisk: 'Elektrisk anlegg',
      vvs: 'VVS-anlegg',
      ventilasjon: 'Ventilasjon',
      brannsikkerhet: 'Brannsikkerhet',
    };
    return typer[type] || type;
  };

  const getTypeHjelpetekst = (type) => {
    const hjelpetekster = {
      yttertak: '(f.eks. "Taktekking: Teglstein", "Papp", "Takstein")',
      yttervegger: '(f.eks. "Kledning: Tre panel", "Plast", "Mur")',
      vinduer_dorer: '(f.eks. "Vinduer: Tre/aluminium", "Dører: Stål")',
      fundament_sokkel: '(f.eks. "Fundament: Betong", "Sokkel: Mur")',
      drenering: '(f.eks. "Type: Innvendig drenering", "Opplegg")',
      elektrisk: '(f.eks. "Sikringsskap: IT", "Jordfeilbryter")',
      vvs: '(f.eks. "Rør: Kobber/plast", "Varmeanlegg: Vannbåren")',
      ventilasjon: '(f.eks. "Type: Balansert", "Mekanisk avtrekk")',
      brannsikkerhet: '(f.eks. "Brannvarslere: Optiske", "Slokkeutstyr")',
    };
    return hjelpetekster[type] || '';
  };

  const getTypePlaceholder = (type) => {
    const placeholders = {
      yttertak: 'F.eks: Taktekking av teglstein med underlaget papp',
      yttervegger: 'F.eks: Kledning av trepanel med vindsperre',
      vinduer_dorer: 'F.eks: Vinduer av tre med aluminiumskledning',
      fundament_sokkel: 'F.eks: Fundament av betong, sokkel av mur',
      drenering: 'F.eks: Innvendig drenering med pumpe',
      elektrisk: 'F.eks: IT-system med jordfeilbryter',
      vvs: 'F.eks: Kobborrør, vannbåren varme',
      ventilasjon: 'F.eks: Balansert ventilasjon med varmegjenvinning',
      brannsikkerhet: 'F.eks: Optiske brannvarslere, pulverapparat',
    };
    return placeholders[type] || 'Beskriv materialer, fabrikater eller konstruksjonstype';
  };

  const renderSporsmalInput = (s) => {
    switch (s.svar_type) {
      case 'ja_nei':
        return (
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name={`svar_${s.id}`}
                value="Ja"
                checked={svar[s.id] === 'Ja'}
                onChange={(e) => handleSvarChange(s.id, s.sporsmal_tekst, e.target.value)}
              />
              Ja
            </label>
            <label>
              <input
                type="radio"
                name={`svar_${s.id}`}
                value="Nei"
                checked={svar[s.id] === 'Nei'}
                onChange={(e) => handleSvarChange(s.id, s.sporsmal_tekst, e.target.value)}
              />
              Nei
            </label>
          </div>
        );

      case 'dropdown':
        let alternativer = [];
        try {
          alternativer = JSON.parse(s.svar_alternativer || '[]');
        } catch (err) {
          console.error('Kunne ikke parse alternativer:', err);
        }

        return (
          <select
            value={svar[s.id] || ''}
            onChange={(e) => handleSvarChange(s.id, s.sporsmal_tekst, e.target.value)}
            className="form-select"
          >
            <option value="">-- Velg --</option>
            {alternativer.map((alt, idx) => (
              <option key={idx} value={alt}>
                {alt}
              </option>
            ))}
          </select>
        );

      case 'tall':
        return (
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={svar[s.id] || ''}
            onChange={(e) => handleSvarChange(s.id, s.sporsmal_tekst, e.target.value)}
            className="form-input"
          />
        );

      case 'tall_med_vet_ikke':
        return (
          <div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={svar[s.id] === 'Vet ikke' ? '' : (svar[s.id] || '')}
                onChange={(e) => handleSvarChange(s.id, s.sporsmal_tekst, e.target.value)}
                className="form-input"
                placeholder="Oppgi alder i år"
                disabled={svar[s.id] === 'Vet ikke'}
                style={{ flex: 1 }}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' }}>
                <input
                  type="checkbox"
                  checked={svar[s.id] === 'Vet ikke'}
                  onChange={(e) => {
                    if (e.target.checked) {
                      handleSvarChange(s.id, s.sporsmal_tekst, 'Vet ikke')
                    } else {
                      handleSvarChange(s.id, s.sporsmal_tekst, '')
                    }
                  }}
                />
                Vet ikke
              </label>
            </div>
            {svar[s.id] && (
              <textarea
                placeholder="Kommentar (valgfritt)"
                value={kommentarer[s.id] || ''}
                onChange={(e) => setKommentarer({ ...kommentarer, [s.id]: e.target.value })}
                rows="2"
                style={{ width: '100%', marginTop: '5px', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
            )}
          </div>
        );

      case 'tekst':
      default:
        return (
          <textarea
            value={svar[s.id] || ''}
            onChange={(e) => handleSvarChange(s.id, s.sporsmal_tekst, e.target.value)}
            className="form-textarea"
            rows={5}
            style={{minHeight: '100px'}}
          />
        );
    }
  };

  if (loading) return <div className="loading">Laster...</div>;
  if (error && !bygningsdel) return <div className="error-message">{error}</div>;
  if (!bygningsdel) return <div className="error-message">Bygningsdel ikke funnet</div>;

  // NOTE: VidereUndersokelser er flyttet til egen side på eiendomsnivå
  return (
    <div className="befaring-page">
      <nav className="navbar">
        <div className="navbar-content">
          <button onClick={() => navigate(`/eiendom/${bygningsdel.eiendom_id}`)} className="btn-back">
            ← Tilbake til bygningsoversikt
          </button>
          <h1>{bygningsdelTypeNavn(bygningsdel.bygningsdel_type)}</h1>
        </div>
      </nav>

      <div className="befaring-content">
        {error && (
          <div className="error-message" style={{ whiteSpace: 'pre-line', marginBottom: '20px' }}>
            {error}
          </div>
        )}

        <div className="befaring-header">
          <h2>
            {bygningsdel.bygningsdel_navn || bygningsdelTypeNavn(bygningsdel.bygningsdel_type)}
            <HelpButton title="Slik fungerer bygningsdelsbefaring" size="medium">
              <p>
                <strong>Bygningsdelsbefaring</strong> er en systematisk gjennomgang av en bygningsdel
                i henhold til NS3600:2018 og Forskrift om tryggere bolighandel §2-18.
              </p>
              <p><strong>Tilstandsgrader (TG):</strong></p>
              <ul>
                <li><strong>TG0</strong> - Ingen merknad / Meget god tilstand</li>
                <li><strong>TG1</strong> - Mindre avvik / God tilstand</li>
                <li><strong>TG2</strong> - Vesentlig avvik / Dårlig tilstand (krever årsak/konsekvens)</li>
                <li><strong>TG3</strong> - Alvorlig avvik / Meget dårlig tilstand (krever årsak/konsekvens + kostnadsanslag)</li>
                <li><strong>TGIU</strong> - Ikke undersøkt (krever begrunnelse)</li>
              </ul>
              <p><strong>Obligatoriske felt:</strong></p>
              <p>
                Spørsmål merket med <span style={{color: 'red'}}>*</span> må besvares eller begrunnes.
                For TG2/TG3 må du beskrive årsak og konsekvens (minimum 50 tegn).
                For TG3 må du også gi kostnadsanslag.
              </p>
              <p><strong>AI-forslag:</strong></p>
              <p>
                Systemet kan foreslå begrunnelser for TGIU og tekster for årsak/konsekvens.
                Du kan velge forslag eller skrive egne beskrivelser.
              </p>
            </HelpButton>
          </h2>
          <p className="befaring-info">
            Befaring basert på §2-18 Forskrift om tryggere bolighandel og NS3600
          </p>

          <p className="befaring-info" style={{ fontSize: '13px', color: '#666', marginTop: '10px' }}>
            Spørsmål merket med <span style={{ color: 'red' }}>*</span> er obligatoriske og må besvares eller begrunnes.
          </p>
        </div>

        {success && (
          <div className="success-message" style={{ margin: '20px 0', padding: '12px', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '5px', color: '#155724' }}>
            {success}
          </div>
        )}

        {sporsmal.length === 0 ? (
          <div className="empty-state">
            <p>⚠️ Ingen spørsmål funnet for denne bygningsdelen.</p>
            <p>Dette kan være en feil i databasen eller spørsmålsbiblioteket.</p>
          </div>
        ) : (
          <div className="sporsmal-container">
            {sporsmal.map((s, index) => {
              // Skjul oppfølgingsspørsmål - de vises nested under foreldrespørsmålet
              if (getOppfolgingMapping[s.id]) {
                return null;
              }

              return (
              <div key={s.id} className="sporsmal-card" style={{
                marginBottom: '30px',
                padding: '20px',
                backgroundColor: '#ffffff',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
              }}>
                <div className="sporsmal-header" style={{marginBottom: '15px'}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px'}}>
                    <span style={{
                      backgroundColor: '#2196F3',
                      color: 'white',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      Spørsmål {index + 1}
                    </span>
                    {s.ns3600_referanse && (
                      <span className="ns3600-ref">{s.ns3600_referanse}</span>
                    )}
                  </div>
                  <h3 style={{marginTop: '5px', marginBottom: '0'}}>
                    <NS3600Tekst tekst={s.sporsmal_tekst} />
                    {s.obligatorisk === 1 && <span style={{ color: 'red' }}> *</span>}
                  </h3>
                </div>

                {renderSporsmalInput(s)}

                {/* TGIU-begrunnelse hvis valgt */}
                {svar[s.id] && svar[s.id].includes('TGIU') && (
                  <div className="tgiu-container"
                       style={{marginTop: '10px', padding: '15px',
                               backgroundColor: '#e3f2fd', borderLeft: '3px solid #2196F3'}}>
                    <p style={{margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold', color: '#1976D2'}}>
                      📋 Årsak til at dette ikke er undersøkt
                    </p>
                    <p style={{margin: '0 0 12px 0', fontSize: '13px', color: '#424242'}}>
                      Etter NS3600:2018 skal det dokumenteres hvorfor en bygningsdel ikke kunne undersøkes:
                    </p>

                    {/* AI-FORSLAG */}
                    {tgiuAiLoading[s.id] && (
                      <p style={{margin: '0 0 10px 0', fontSize: '13px', color: '#1976D2'}}>
                        🤖 Henter AI-forslag basert på spørsmål...
                      </p>
                    )}

                    {tgiuAiForslag[s.id] && tgiuAiForslag[s.id].length > 0 && (
                      <div style={{marginBottom: '12px'}}>
                        <p style={{margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold', color: '#1976D2'}}>
                          🤖 AI-forslag (klikk for å bruke):
                        </p>
                        {tgiuAiForslag[s.id].slice(0, 3).map((forslag, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => {
                              setTgiuBegrunnelse({
                                ...tgiuBegrunnelse,
                                [s.id]: forslag.standardtekst
                              });
                              registrerTgiuBruk(forslag.id);
                            }}
                            style={{
                              display: 'block',
                              width: '100%',
                              marginBottom: '6px',
                              padding: '8px 12px',
                              fontSize: '12px',
                              textAlign: 'left',
                              backgroundColor: idx === 0 ? '#4CAF50' : '#2196F3',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            {idx === 0 ? '⭐' : '💡'} {forslag.standardtekst.substring(0, 80)}...
                            <br />
                            <span style={{fontSize: '11px', opacity: 0.9}}>
                              Score: {forslag.score}/100 • {forslag.kategori}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}

                    <textarea
                      value={tgiuBegrunnelse[s.id] || ''}
                      onChange={(e) => setTgiuBegrunnelse({
                        ...tgiuBegrunnelse, [s.id]: e.target.value
                      })}
                      placeholder="F.eks: Ikke tilgjengelig for inspeksjon, dekket av møbler, krever destruktiv undersøkelse, etc. (minimum 10 tegn)"
                      style={{ width: '100%', minHeight: '80px', padding: '8px',
                               border: '1px solid #2196F3', borderRadius: '4px',
                               fontFamily: 'inherit', fontSize: '14px' }}
                    />
                    {(!tgiuBegrunnelse[s.id] || tgiuBegrunnelse[s.id].length < 10) && (
                      <p style={{margin: '5px 0 0 0', fontSize: '12px', color: '#1565C0'}}>
                        Begrunnelse påkrevd (minimum 10 tegn) {tgiuBegrunnelse[s.id] ? `(${tgiuBegrunnelse[s.id].length}/10)` : '(0/10)'}
                      </p>
                    )}
                  </div>
                )}

                {/* Kommentar-felt for ALLE spørsmål (valgfritt) */}
                {svar[s.id] && !svar[s.id].includes('IU') && (
                  <div style={{marginTop: '10px'}}>
                    <label style={{fontSize: '14px', fontWeight: '500', display: 'block', marginBottom: '5px', color: '#666'}}>
                      💬 Kommentar (valgfritt)
                    </label>
                    <textarea
                      value={kommentarer[s.id] || ''}
                      onChange={(e) => setKommentarer({...kommentarer, [s.id]: e.target.value})}
                      placeholder="Tilleggskommentar eller presiseringer..."
                      style={{width: '100%', minHeight: '50px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontFamily: 'inherit', fontSize: '14px'}}
                    />
                  </div>
                )}

                {/* §2-22: Årsak og konsekvens for TG2/TG3 (obligatorisk) */}
                {svar[s.id] && (svar[s.id].includes('TG2') || svar[s.id].includes('TG3')) && (
                  <div style={{marginTop: '10px', padding: '15px', backgroundColor: '#fff3cd', borderLeft: '3px solid #ff9800'}}>
                    <p style={{margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold', color: '#f57c00'}}>
                      ⚠️ §2-22: Årsak og konsekvens (obligatorisk for TG2/TG3)
                    </p>
                    <p style={{margin: '0 0 8px 0', fontSize: '13px', color: '#424242'}}>
                      Forskriften krever beskrivelse av hva som har forårsaket tilstanden og hvilke konsekvenser dette har:
                    </p>
                    <textarea
                      value={aarsakKonsekvens[s.id] || ''}
                      onChange={(e) => setAarsakKonsekvens({...aarsakKonsekvens, [s.id]: e.target.value})}
                      onBlur={(e) => validateAarsakKonsekvens(s.id, e.target.value, s)}
                      placeholder="Beskriv årsak til tilstanden og konsekvenser for bygningen (minimum 50 tegn)..."
                      style={{width: '100%', minHeight: '80px', padding: '8px', border: '1px solid #ff9800', borderRadius: '4px', fontFamily: 'inherit', fontSize: '14px'}}
                    />
                    {(!aarsakKonsekvens[s.id] || aarsakKonsekvens[s.id].length < 50) && (
                      <p style={{margin: '5px 0 0 0', fontSize: '12px', color: '#e65100'}}>
                        Beskrivelse påkrevd (minimum 50 tegn) {aarsakKonsekvens[s.id] ? `(${aarsakKonsekvens[s.id].length}/50)` : '(0/50)'}
                      </p>
                    )}

                    {/* AI-validering feedback */}
                    {aiValiderer[s.id] && (
                      <div style={{marginTop: '8px', padding: '8px', backgroundColor: '#e3f2fd', borderRadius: '4px', fontSize: '13px'}}>
                        🤖 AI vurderer beskrivelsen...
                      </div>
                    )}

                    {aiValidering[s.id] && !aiValiderer[s.id] && (
                      <div style={{
                        marginTop: '8px',
                        padding: '10px',
                        backgroundColor: aiValidering[s.id].score >= 7 ? '#e8f5e9' : aiValidering[s.id].score >= 5 ? '#fff3e0' : '#ffebee',
                        borderLeft: `3px solid ${aiValidering[s.id].score >= 7 ? '#4caf50' : aiValidering[s.id].score >= 5 ? '#ff9800' : '#f44336'}`,
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}>
                        <div style={{fontWeight: 'bold', marginBottom: '5px'}}>
                          🤖 AI-vurdering: {aiValidering[s.id].score}/10
                        </div>
                        <div>{aiValidering[s.id].feedback}</div>

                        {/* Vis forbedringsforslag hvis score < 8 */}
                        {aiValidering[s.id].improvement_suggestion && aiValidering[s.id].score < 8 && (
                          <div style={{
                            marginTop: '10px',
                            padding: '10px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '4px',
                            borderLeft: '3px solid #2196f3'
                          }}>
                            <div style={{fontWeight: 'bold', marginBottom: '5px', color: '#2196f3'}}>
                              💡 Forbedringsforslag:
                            </div>
                            <div style={{marginBottom: '8px', fontStyle: 'italic'}}>
                              {aiValidering[s.id].improvement_suggestion}
                            </div>
                            <button
                              onClick={() => {
                                const textarea = document.querySelector(`textarea[placeholder*="årsak"][placeholder*="konsekvens"]`);
                                if (textarea) {
                                  textarea.value = aiValidering[s.id].improvement_suggestion;
                                  textarea.dispatchEvent(new Event('input', { bubbles: true }));
                                  // Trigger re-validation
                                  validateAarsakKonsekvens(s.id, aiValidering[s.id].improvement_suggestion);
                                }
                              }}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#2196f3',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: 'bold'
                              }}
                            >
                              ✅ Bruk dette forslaget
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* §2-22: Kostnadsanslag for TG3 (obligatorisk) */}
                {svar[s.id] && svar[s.id].includes('TG3') && (
                  <div style={{marginTop: '10px', padding: '15px', backgroundColor: '#ffebee', borderLeft: '3px solid #f44336'}}>
                    <p style={{margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold', color: '#c62828'}}>
                      💰 Kostnadsanslag (obligatorisk for TG3)
                    </p>
                    <p style={{margin: '0 0 12px 0', fontSize: '12px', color: '#666', fontStyle: 'italic'}}>
                      ℹ️ Bruk sjablongmessige intervaller basert på bransjenormer (Norsk Takst)
                    </p>

                    <label style={{display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#424242'}}>
                      Kostnadsintervall:
                    </label>
                    <select
                      value={kostnadsanslag[s.id]?.kategori || ''}
                      onChange={(e) => {
                        const ny = {...kostnadsanslag};
                        if (!ny[s.id]) ny[s.id] = {};
                        ny[s.id].kategori = e.target.value;
                        if (e.target.value !== 'eksakt') ny[s.id].eksakt = '';
                        setKostnadsanslag(ny);
                      }}
                      style={{width: '100%', padding: '8px', border: '1px solid #f44336', borderRadius: '4px', fontFamily: 'inherit', fontSize: '14px', marginBottom: '10px'}}
                    >
                      <option value="">-- Velg intervall --</option>
                      <option value="under_10k">Under 10 000 kr</option>
                      <option value="10k_50k">10 000 – 50 000 kr</option>
                      <option value="50k_100k">50 000 – 100 000 kr</option>
                      <option value="100k_300k">100 000 – 300 000 kr</option>
                      <option value="over_300k">Over 300 000 kr</option>
                      <option value="eksakt">Eksakt beløp (ved konkret anbud)</option>
                    </select>

                    {kostnadsanslag[s.id]?.kategori === 'eksakt' && (
                      <div style={{marginBottom: '10px'}}>
                        <label style={{display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#424242'}}>
                          Eksakt beløp (NOK):
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={kostnadsanslag[s.id]?.eksakt || ''}
                          onChange={(e) => {
                            const ny = {...kostnadsanslag};
                            if (!ny[s.id]) ny[s.id] = {};
                            ny[s.id].eksakt = e.target.value;
                            setKostnadsanslag(ny);
                          }}
                          placeholder="Eksakt kostnad i NOK"
                          style={{width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontFamily: 'inherit', fontSize: '14px'}}
                        />
                      </div>
                    )}

                    <label style={{display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#424242'}}>
                      Hva omfatter estimatet:
                    </label>
                    <textarea
                      value={kostnadsanslag[s.id]?.beskrivelse || ''}
                      onChange={(e) => {
                        const ny = {...kostnadsanslag};
                        if (!ny[s.id]) ny[s.id] = {};
                        ny[s.id].beskrivelse = e.target.value;
                        setKostnadsanslag(ny);
                      }}
                      placeholder="F.eks. 'Utbedring inkluderer materialer, arbeidskostnader og riving av gammel konstruksjon'"
                      rows="2"
                      style={{width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontFamily: 'inherit', fontSize: '13px', marginBottom: '10px'}}
                    />

                    <label style={{display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#424242'}}>
                      Usikkerhetsnivå:
                    </label>
                    <select
                      value={kostnadsanslag[s.id]?.usikkerhet || 'middels'}
                      onChange={(e) => {
                        const ny = {...kostnadsanslag};
                        if (!ny[s.id]) ny[s.id] = {};
                        ny[s.id].usikkerhet = e.target.value;
                        setKostnadsanslag(ny);
                      }}
                      style={{width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontFamily: 'inherit', fontSize: '14px', marginBottom: '10px'}}
                    >
                      <option value="lav">Lav - Basert på konkret anbud/erfaring</option>
                      <option value="middels">Middels - Basert på erfaringstall</option>
                      <option value="høy">Høy - Grovt anslag, krever nærmere undersøkelse</option>
                    </select>

                    <label style={{display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#424242'}}>
                      Anbefaling (valgfritt):
                    </label>
                    <textarea
                      value={kostnadsanslag[s.id]?.anbefaling || ''}
                      onChange={(e) => {
                        const ny = {...kostnadsanslag};
                        if (!ny[s.id]) ny[s.id] = {};
                        ny[s.id].anbefaling = e.target.value;
                        setKostnadsanslag(ny);
                      }}
                      placeholder="F.eks. 'Anbefales å innhente konkrete anbud fra minst 3 autoriserte entreprenører'"
                      rows="2"
                      style={{width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontFamily: 'inherit', fontSize: '13px'}}
                    />

                    {(!kostnadsanslag[s.id]?.kategori) && (
                      <p style={{margin: '10px 0 0 0', fontSize: '12px', color: '#b71c1c'}}>
                        ⚠️ Kostnadsintervall må velges
                      </p>
                    )}
                  </div>
                )}

                {/* BILDESEKSJON: Spørsmål-spesifikke bilder for ALLE besvarte spørsmål */}
                {svar[s.id] && !svar[s.id].includes('IU') && (
                  <div className="sporsmal-bilde-seksjon" style={{
                    marginTop: '20px',
                    padding: '15px',
                    backgroundColor: (svar[s.id] === 'TG2' || svar[s.id] === 'TG3') ? '#fff3e0' : '#f0f8ff',
                    borderRadius: '6px',
                    border: (svar[s.id] === 'TG2' || svar[s.id] === 'TG3') ? '2px solid #ff9800' : '2px solid #2196f3'
                  }}>
                    <div style={{marginBottom: '12px'}}>
                      <h5 style={{margin: '0 0 5px 0', fontSize: '15px', color: '#333'}}>
                        📸 {(svar[s.id] === 'TG2' || svar[s.id] === 'TG3') ? 'Dokumenter mangel/skade' : 'Legg til bilder'}
                      </h5>
                      <p style={{margin: '0', color: '#666', fontSize: '12px', fontStyle: 'italic'}}>
                        Knyttet til: "{s.sporsmal_tekst}"
                      </p>
                      <p style={{color: '#666', fontSize: '13px', marginTop: '8px', marginBottom: '0'}}>
                        {(svar[s.id] === 'TG2' || svar[s.id] === 'TG3')
                          ? 'Last opp bilder som dokumenterer skaden/mangelen (obligatorisk for TG2/TG3)'
                          : 'Valgfritt: Last opp bilder som viser aktuelle forhold'
                        }
                      </p>
                    </div>

                    <BildeOpplaster
                      type="bygningsdel"
                      id={id}
                      sporsmalId={s.id}
                      bildeposisjon="sporsmal"
                      placeholder={`Ta bilde - ${s.sporsmal_tekst}`}
                      onUploadSuccess={() => fetchSporsmalBilder(s.id)}
                    />

                    <BildeGalleri
                      bilder={sporsmalBilder[s.id] || []}
                      onRefresh={() => fetchSporsmalBilder(s.id)}
                    />
                  </div>
                )}

                {s.obligatorisk && (!svar[s.id] || svar[s.id].trim() === '') && (
                  <div className="ikke-besvart-container" style={{marginTop: '10px'}}>
                    {/* Vis gul varselboks kun før brukeren begynner å skrive */}
                    {(!ikkeBesvartBegrunnelse[s.id] || ikkeBesvartBegrunnelse[s.id].trim() === '') ? (
                      <div style={{padding: '10px', backgroundColor: '#fff3cd', borderLeft: '3px solid #ffc107', marginBottom: '10px'}}>
                        <p style={{margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold'}}>
                          ⚠️ Dette spørsmålet er obligatorisk
                        </p>
                        <p style={{margin: '0', fontSize: '13px'}}>
                          Enten besvar spørsmålet ovenfor, eller forklar hvorfor det ikke kan besvares nedenfor.
                        </p>
                      </div>
                    ) : (
                      <div style={{marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '5px'}}>
                        <span style={{fontSize: '12px', color: '#666', fontStyle: 'italic'}}>
                          Obligatorisk begrunnelse (må ikke besvares)
                        </span>
                        <button
                          type="button"
                          onClick={() => setVisParagraf218Info(!visParagraf218Info)}
                          style={{background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#666'}}
                          title="Klikk for mer informasjon om obligatoriske spørsmål"
                        >
                          ℹ️
                        </button>
                      </div>
                    )}

                    {/* § 2-18 info-boks */}
                    {visParagraf218Info && (
                      <div style={{
                        marginBottom: '15px',
                        padding: '15px',
                        backgroundColor: '#e3f2fd',
                        border: '2px solid #2196f3',
                        borderRadius: '8px'
                      }}>
                        <h4 style={{margin: '0 0 10px 0', fontSize: '15px', color: '#1565c0'}}>
                          📘 § 2-18 Obligatoriske kontroller
                        </h4>
                        <p style={{margin: '0 0 10px 0', fontSize: '13px', lineHeight: '1.6'}}>
                          <strong>Forskrift om tryggere bolighandel § 2-18</strong> pålegger takstmann å kontrollere spesifikke bygningsdeler som erfaringsmessig har høy risiko for feil og mangler.
                        </p>
                        <p style={{margin: '0 0 10px 0', fontSize: '13px', lineHeight: '1.6'}}>
                          <strong>Hvorfor obligatorisk begrunnelse?</strong>
                        </p>
                        <ul style={{margin: '0 0 10px 0', paddingLeft: '20px', fontSize: '13px', lineHeight: '1.6'}}>
                          <li>Kontrollen er <strong>lovpålagt</strong> - den kan ikke utelates uten gyldig grunn</li>
                          <li>Hvis kontrollen ikke kan gjennomføres (f.eks. skjult av konstruksjon, ikke tilgjengelig), må dette dokumenteres</li>
                          <li>Begrunnelsen sikrer at kjøper forstår hvorfor kontrollen ikke er utført</li>
                          <li>Dette er en del av takstmannens yrkesansvar og aktsomhetsplikt</li>
                        </ul>
                        <p style={{margin: '0', fontSize: '12px', color: '#0d47a1', fontStyle: 'italic'}}>
                          💡 Eksempler på gyldige begrunnelser: "Hulltakinstøtende vegger ikke tilgjengelig for inspeksjon grunnet ferdig overflatebehandling", "Kontroll av tetningsmembran ikke mulig uten nedrivning av veggpanel"
                        </p>
                        <button
                          type="button"
                          onClick={() => setVisParagraf218Info(false)}
                          style={{
                            marginTop: '12px',
                            padding: '6px 12px',
                            backgroundColor: '#2196f3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Lukk
                        </button>
                      </div>
                    )}

                    <textarea
                      value={ikkeBesvartBegrunnelse[s.id] || ''}
                      onChange={(e) => setIkkeBesvartBegrunnelse({
                        ...ikkeBesvartBegrunnelse, [s.id]: e.target.value
                      })}
                      placeholder="F.eks: Ikke tilgjengelig for inspeksjon, skjult av konstruksjon, etc. (minimum 10 tegn)"
                      style={{
                        width: '100%',
                        minHeight: '60px',
                        padding: '8px',
                        border: (!ikkeBesvartBegrunnelse[s.id] || ikkeBesvartBegrunnelse[s.id].trim() === '')
                          ? '2px solid #ffc107'  // Gul kant før skrivstart
                          : '1px solid #ddd',     // Normal kant når de skriver
                        borderRadius: '4px',
                        fontFamily: 'inherit',
                        fontSize: '14px'
                      }}
                    />
                    {ikkeBesvartBegrunnelse[s.id] && ikkeBesvartBegrunnelse[s.id].length < 10 && (
                      <p style={{margin: '5px 0 0 0', fontSize: '12px', color: '#856404'}}>
                        Begrunnelsen må være minst 10 tegn ({ikkeBesvartBegrunnelse[s.id].length}/10)
                      </p>
                    )}
                  </div>
                )}

                {/* Nested oppfølgingsspørsmål */}
                {(() => {
                  // Finn alle oppfølgingsspørsmål for dette hovedspørsmålet
                  const nestedQuestions = sporsmal.filter(nested =>
                    getOppfolgingMapping[nested.id]?.parentId === s.id
                  );

                  return nestedQuestions.map((nestedQ) => {
                    const mapping = getOppfolgingMapping[nestedQ.id];

                    let shouldShow = false;
                    if (mapping.showWhen === 'any') {
                      shouldShow = svar[s.id] && svar[s.id].trim() !== '';
                    } else if (mapping.showWhen === 'lessThan5') {
                      // Vis dokumentasjonsspørsmål kun hvis alder < 5 år
                      const alder = parseInt(svar[s.id]);
                      shouldShow = !isNaN(alder) && alder > 0 && alder < 5;
                    } else if (mapping.multipleParents) {
                      shouldShow = mapping.multipleParents.some(parentId => svar[parentId] === mapping.showWhen);
                    } else {
                      shouldShow = svar[s.id] === mapping.showWhen;
                    }

                    if (!shouldShow) return null;

                    return (
                      <div key={nestedQ.id} style={{
                        marginTop: '15px',
                        padding: '15px',
                        backgroundColor: '#f0f8ff',
                        border: '2px solid #2196f3',
                        borderRadius: '8px'
                      }}>
                        <h4 style={{margin: '0 0 10px 0', fontSize: '15px', color: '#1976d2'}}>
                          ↪️ <NS3600Tekst tekst={nestedQ.sporsmal_tekst} />
                          {nestedQ.obligatorisk === 1 && <span style={{ color: 'red' }}> *</span>}
                        </h4>

                        {/* Info-boks for hulltaking */}
                        {nestedQ.sporsmal_tekst.toLowerCase().includes('hulltaking') &&
                         nestedQ.sporsmal_tekst.toLowerCase().includes('resultat') && (
                          <div style={{
                            marginBottom: '12px',
                            padding: '12px',
                            backgroundColor: '#e3f2fd',
                            border: '1px solid #64b5f6',
                            borderRadius: '6px'
                          }}>
                            <p style={{margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold', color: '#1565c0'}}>
                              📋 Hva skal undersøkes ved hulltaking?
                            </p>
                            <ul style={{margin: '0', paddingLeft: '20px', fontSize: '13px', lineHeight: '1.6', color: '#424242'}}>
                              <li><strong>Tetningsmembran:</strong> Type, tilstand, feste til vegg og gulv</li>
                              <li><strong>Fukt og skader:</strong> Tegn på vanninntrengning, råteskader, mugg</li>
                              <li><strong>Konstruksjon:</strong> Hulltakinstøtende vegger, isolasjon, dampsperre</li>
                              <li><strong>Dokumentasjon:</strong> Ta bilder av åpningen og funn</li>
                            </ul>
                          </div>
                        )}

                        {renderSporsmalInput(nestedQ)}
                      </div>
                    );
                  });
                })()}
              </div>
              );
            })}
          </div>
        )}

        {/* Bygningsdel-metadata seksjon (valgfrie opplysninger) */}
        <div style={{marginTop: '40px', marginBottom: '25px', padding: '15px', backgroundColor: '#fafafa', borderRadius: '8px', border: '1px solid #e0e0e0'}}>
          <h3 style={{marginTop: '0', marginBottom: '15px', fontSize: '16px', color: '#333'}}>
            ℹ️ Tilleggsinformasjon (valgfritt)
          </h3>

          {/* Type-spesifikasjon felt */}
          <div style={{marginBottom: '15px'}}>
            <label style={{display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#333'}}>
              📝 Spesifiser type/detaljer {getTypeHjelpetekst(bygningsdel.bygningsdel_type)}
            </label>
            <input
              type="text"
              value={typeKommentar}
              onChange={(e) => setTypeKommentar(e.target.value)}
              placeholder={getTypePlaceholder(bygningsdel.bygningsdel_type)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #b3d9ff',
                borderRadius: '4px',
                fontSize: '14px',
                fontFamily: 'inherit'
              }}
            />
            <small style={{color: '#666', fontSize: '12px', marginTop: '5px', display: 'block'}}>
              Valgfritt felt for å beskrive spesifikke materialer, fabrikater eller konstruksjonstype
            </small>
          </div>

          {/* Renoveringsår felt for TEK-vurdering */}
          <div style={{marginBottom: '15px'}}>
            <label style={{display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#333'}}>
              📅 Renoveringsår (valgfritt)
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={renoveringsAar}
              onChange={(e) => setRenoveringsAar(e.target.value)}
              placeholder="F.eks. 2011"
              style={{
                width: '200px',
                padding: '8px',
                border: '1px solid #ffd699',
                borderRadius: '4px',
                fontSize: '14px',
                fontFamily: 'inherit'
              }}
            />
            <small style={{color: '#666', fontSize: '12px', marginTop: '5px', display: 'block'}}>
              Hvis bygningsdelen er renovert etter byggeår. Brukes for TEK-vurdering i rapport.
            </small>
          </div>

          {/* Live TEK-preview */}
          {tekVeiledning && (
            <div style={{
              marginTop: '5px',
              padding: '12px',
              backgroundColor: tekVeiledning.erRenovert ? '#e8f5e9' : '#f5f5f5',
              borderRadius: '5px',
              border: `1px solid ${tekVeiledning.farge}`
            }}>
              <div style={{display: 'flex', alignItems: 'center', marginBottom: '10px'}}>
                <span style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  backgroundColor: tekVeiledning.farge,
                  color: 'white',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  fontSize: '13px',
                  marginRight: '10px'
                }}>
                  {tekVeiledning.versjon}
                </span>
                <span style={{fontSize: '14px', color: '#333'}}>
                  {tekVeiledning.statusTekst}
                </span>
              </div>

              <div style={{marginTop: '12px'}}>
                <p style={{margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold', color: '#555'}}>
                  📋 Relevante paragrafer:
                </p>
                <ul style={{margin: '0', paddingLeft: '20px', fontSize: '12px', color: '#666'}}>
                  {tekVeiledning.paragrafer.map((p, idx) => (
                    <li key={idx} style={{marginBottom: '3px'}}>{p}</li>
                  ))}
                </ul>
              </div>

              <div style={{marginTop: '12px', padding: '10px', backgroundColor: 'rgba(255,255,255,0.7)', borderRadius: '4px'}}>
                <p style={{margin: '0 0 6px 0', fontSize: '13px', fontWeight: 'bold', color: '#333'}}>
                  ⚙️ Hovedkrav:
                </p>
                <p style={{margin: '0', fontSize: '12px', color: '#555', lineHeight: '1.5'}}>
                  {tekVeiledning.hovedkrav}
                </p>
              </div>

              <div style={{marginTop: '12px'}}>
                <p style={{margin: '0 0 8px 0', fontSize: '13px', fontWeight: 'bold', color: '#555'}}>
                  ✅ Testpunkter ved befaring:
                </p>
                <ul style={{margin: '0', paddingLeft: '20px', fontSize: '12px', color: '#666'}}>
                  {tekVeiledning.testpunkter.map((t, idx) => (
                    <li key={idx} style={{marginBottom: '3px'}}>{t}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Bildeopplasting seksjon */}
        <div className="bilder-seksjon" style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
          <h3 style={{ marginBottom: '15px' }}>📷 Dokumentasjon med bilder</h3>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
            Last opp bilder for å dokumentere tilstanden til denne bygningsdelen.
          </p>

          <div style={{ marginBottom: '20px' }}>
            <input
              type="file"
              accept="image/*"
              onChange={handleBildeUpload}
              disabled={uploadingBilde}
              style={{ display: 'none' }}
              id="bilde-upload"
            />
            <label
              htmlFor="bilde-upload"
              style={{
                display: 'inline-block',
                padding: '10px 20px',
                backgroundColor: uploadingBilde ? '#ccc' : '#2196F3',
                color: 'white',
                borderRadius: '4px',
                cursor: uploadingBilde ? 'not-allowed' : 'pointer',
                fontWeight: '500'
              }}
            >
              {uploadingBilde ? '⏳ Laster opp...' : '📸 Last opp bilde'}
            </label>
          </div>

          {bilder.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
              {bilder.map((bilde) => (
                <div
                  key={bilde.id}
                  style={{
                    position: 'relative',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  <img
                    src={bilde.cloudflare_r2_url}
                    alt={bilde.filnavn}
                    style={{
                      width: '100%',
                      height: '150px',
                      objectFit: 'cover'
                    }}
                  />
                  <div style={{ padding: '10px' }}>
                    <p style={{ fontSize: '12px', color: '#666', margin: '0 0 8px 0', wordBreak: 'break-word' }}>
                      {bilde.filnavn}
                    </p>
                    <button
                      onClick={() => handleSlettBilde(bilde.id)}
                      style={{
                        width: '100%',
                        padding: '6px',
                        backgroundColor: '#f44336',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      🗑️ Slett
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontSize: '14px', color: '#999', fontStyle: 'italic' }}>
              Ingen bilder lastet opp ennå
            </p>
          )}
        </div>

        <div className="befaring-actions">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary btn-large"
          >
            {saving ? 'Lagrer...' : 'Lagre'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BygningsdelBefaring;
